import {
  CommoditiesForwardCurvesResponse,
  CommoditiesVolatilityCurvesResponse,
  ForeignExchangeAnalysisResponse,
  ForwardCurveResponse,
  HistoricalForwardCurvesResponse,
  InterestCostCalculationsResponse,
  InterestRateAnalysisCurvesResponse,
  MarketOverviewCurrencyRateResponse,
  MarketOverviewInterestRateResponse,
} from '@/store/marketData/types';
import { PayloadAction, createSlice } from '@reduxjs/toolkit';

import {
  checkCommodityAvailability,
  fetchCommodityIndices,
  fetchCurrencyPair,
  fetchIrIndexList,
  fetchMarketDataCurrencies,
  fetchSecurities,
  getCommoditiesForwardCurvesData,
  getCommoditiesVolatilityCurvesData,
  getForeignExchangeAnalysisData,
  getForwardCurvesData,
  getFxForwardCurvesData,
  getHistoricalForwardCurvesData,
  getInterestCostCalculationsData,
  getInterestRateAnalysisCurvesData,
  getMarketOverviewCurrencyRatesData,
  getMarketOverviewInterestRatesData,
  validateFxCurrency,
  validateIrIndex,
} from './thunks';
import {
  CheckCommodityAvailabilityResult,
  CommodityIndex,
  Currency,
  CurrencyPairTypes,
  CurrencyResponse,
  FxForwardCurvePairData,
  IrIndex,
  MarketDataState,
  SecuritiesTypes,
  ValidateFxCurrencyResult,
  ValidateIrIndexResult,
} from './types';

const initialState: MarketDataState = {
  irIndexList: [],
  irIndexOptions: [],
  irIndexListLoading: false,
  irIndexListError: null,
  commodityIndices: [],
  commodityIndicesLoading: false,
  commodityIndicesError: null,
  currencies: [],
  currenciesLoading: false,
  currenciesError: null,
  commodityAvailability: null,
  commodityAvailabilityLoading: false,
  commodityAvailabilityError: null,
  fxCurrencyValidation: null,
  fxCurrencyValidationLoading: false,
  fxCurrencyValidationError: null,
  validateIrIndexResult: null,
  validateIrIndexLoading: false,
  validateIrIndexError: null,
  currencyPairResult: null,
  currencyPairLoading: false,
  currencyPairError: null,
  securitiesResult: null,
  securitiesLoading: false,
  securitiesError: null,
  cdsCurveReport: {
    data: null,
    loading: false,
    error: null,
    successMessage: null,
    errorMessage: null,
  },
  saveInputsLoading: false,
  dealContingentResult: {
    file_name: '',
    file_path: '',
    file_s3_url: '',
  },
  dealContingentLoading: false,
  dealContingentError: '',
  ForwardCurvesDataState: {
    title: '',
    valuation_date: '',
    indices: [],
    data: [],
    loading: false,
    error: null,
  },
  HistoricalForwardCurvesDataState: {
    title: '',
    description: '',
    data: [],
    meta: {
      currency_id: 0,
      security_id: 0,
      num_run_dates: 0,
    },
    loading: false,
    error: null,
  },
  InterestRateAnalysisCurvesDataState: {
    data: [],
    loading: false,
    error: null,
  },
  InterestCostCalculationsDataState: {
    loading: false,
    error: null,
    data: [],
    summary: null,
  },
  ForeignExchangeAnalysisDataState: {
    loading: false,
    error: null,
    summary: null,
    statisticsData: null,
    projectionsData: null,
    chartData: null,
    forwardRatesData: [],
    optionPricingData: [],
    spotRates: [],
  },
  CommoditiesForwardCurvesDataState: {
    loading: false,
    error: null,
    summary: null,
    data: [],
  },
  CommoditiesVolatilityCurvesDataState: {
    loading: false,
    error: null,
    summary: null,
    data: [],
  },
  FxForwardCurvesDataState: {
    loading: false,
    error: null,
    run_date: null,
    pairs: [],
  },
  MarketOverviewCurrencyRateState: {
    loading: false,
    error: null,
    data: null,
  },
  MarketOverviewInterestRateState: {
    loading: false,
    error: null,
    data: null,
  },
};

const marketDataSlice = createSlice({
  name: 'marketData',
  initialState,
  reducers: {
    clearFxCurrencyValidation(state) {
      state.fxCurrencyValidation = null;
      state.fxCurrencyValidationError = null;
    },
    clearValidateIrIndexResult(state) {
      state.validateIrIndexResult = null;
      state.validateIrIndexError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchIrIndexList.pending, (state) => {
        state.irIndexListLoading = true;
        state.irIndexListError = null;
      })
      .addCase(
        fetchIrIndexList.fulfilled,
        (state, action: PayloadAction<IrIndex[]>) => {
          state.irIndexListLoading = false;
          state.irIndexList = action.payload;
          state.irIndexOptions = action.payload.map((item) => ({
            label: item.index_desc,
            value: String(item.index_id),
          }));
        },
      )
      .addCase(fetchIrIndexList.rejected, (state, action) => {
        state.irIndexListLoading = false;
        state.irIndexListError = action.payload as string;
      })
      .addCase(fetchCommodityIndices.pending, (state) => {
        state.commodityIndicesLoading = true;
        state.commodityIndicesError = null;
      })
      .addCase(
        fetchCommodityIndices.fulfilled,
        (state, action: PayloadAction<CommodityIndex[]>) => {
          state.commodityIndicesLoading = false;
          state.commodityIndices = action.payload;
        },
      )
      .addCase(fetchCommodityIndices.rejected, (state, action) => {
        state.commodityIndicesLoading = false;
        state.commodityIndicesError = action.payload as string;
      })
      .addCase(fetchMarketDataCurrencies.pending, (state) => {
        state.currenciesLoading = true;
        state.currenciesError = null;
      })
      .addCase(
        fetchMarketDataCurrencies.fulfilled,
        (state, action: PayloadAction<Currency[]>) => {
          state.currenciesLoading = false;
          state.currencies = action.payload;
        },
      )
      .addCase(fetchMarketDataCurrencies.rejected, (state, action) => {
        state.currenciesLoading = false;
        state.currenciesError = action.payload as string;
      })
      .addCase(checkCommodityAvailability.pending, (state) => {
        state.commodityAvailabilityLoading = true;
        state.commodityAvailabilityError = null;
      })
      .addCase(
        checkCommodityAvailability.fulfilled,
        (state, action: PayloadAction<CheckCommodityAvailabilityResult>) => {
          state.commodityAvailabilityLoading = false;
          state.commodityAvailability = action.payload;
        },
      )
      .addCase(checkCommodityAvailability.rejected, (state, action) => {
        state.commodityAvailabilityLoading = false;
        state.commodityAvailabilityError = action.payload as string;
      })
      .addCase(validateFxCurrency.pending, (state) => {
        state.fxCurrencyValidationLoading = true;
        state.fxCurrencyValidationError = null;
      })
      .addCase(
        validateFxCurrency.fulfilled,
        (state, action: PayloadAction<ValidateFxCurrencyResult>) => {
          state.fxCurrencyValidationLoading = false;
          state.fxCurrencyValidation = action.payload;
        },
      )
      .addCase(validateFxCurrency.rejected, (state, action) => {
        state.fxCurrencyValidationLoading = false;
        state.fxCurrencyValidationError = action.payload as string;
      })
      .addCase(validateIrIndex.pending, (state) => {
        state.validateIrIndexLoading = true;
        state.validateIrIndexError = null;
      })
      .addCase(
        validateIrIndex.fulfilled,
        (state, action: PayloadAction<ValidateIrIndexResult>) => {
          state.validateIrIndexLoading = false;
          state.validateIrIndexResult = action.payload;
        },
      )
      .addCase(validateIrIndex.rejected, (state, action) => {
        state.validateIrIndexLoading = false;
        state.validateIrIndexError = action.payload as string;
      })
      .addCase(fetchCurrencyPair.pending, (state) => {
        state.currencyPairLoading = true;
        state.currencyPairError = null;
      })
      .addCase(
        fetchCurrencyPair.fulfilled,
        (state, action: PayloadAction<CurrencyPairTypes[]>) => {
          state.currencyPairLoading = false;
          state.currencyPairResult = action.payload;
        },
      )
      .addCase(fetchCurrencyPair.rejected, (state, action) => {
        state.currencyPairLoading = false;
        state.currencyPairError = action.payload as string;
      })
      .addCase(fetchSecurities.pending, (state) => {
        state.securitiesLoading = true;
        state.securitiesError = null;
      })
      .addCase(
        fetchSecurities.fulfilled,
        (state, action: PayloadAction<SecuritiesTypes[]>) => {
          state.securitiesLoading = false;
          state.securitiesResult = action.payload;
        },
      )
      .addCase(fetchSecurities.rejected, (state, action) => {
        state.securitiesLoading = false;
        state.securitiesError = action.payload as string;
      })
      .addCase(getForwardCurvesData.pending, (state) => {
        state.ForwardCurvesDataState.loading = true;
        state.ForwardCurvesDataState.error = null;
      })
      .addCase(
        getForwardCurvesData.fulfilled,
        (state, action: PayloadAction<ForwardCurveResponse>) => {
          state.ForwardCurvesDataState.data = action.payload.data?.data;
          state.ForwardCurvesDataState.loading = false;
        },
      )
      .addCase(getForwardCurvesData.rejected, (state, action) => {
        state.ForwardCurvesDataState.loading = false;
        state.ForwardCurvesDataState.error = action.payload as string;
        state.ForwardCurvesDataState.data = [];
      })
      .addCase(getHistoricalForwardCurvesData.pending, (state) => {
        state.HistoricalForwardCurvesDataState.loading = true;
        state.HistoricalForwardCurvesDataState.error = null;
      })
      .addCase(
        getHistoricalForwardCurvesData.fulfilled,
        (state, action: PayloadAction<HistoricalForwardCurvesResponse>) => {
          state.HistoricalForwardCurvesDataState.title =
            action.payload.data.title;
          state.HistoricalForwardCurvesDataState.description =
            action.payload.data.description;
          state.HistoricalForwardCurvesDataState.data =
            action.payload.data?.curves;
          state.HistoricalForwardCurvesDataState.loading = false;
          state.HistoricalForwardCurvesDataState.meta =
            action.payload.data.meta;
        },
      )
      .addCase(getHistoricalForwardCurvesData.rejected, (state, action) => {
        state.HistoricalForwardCurvesDataState.loading = false;
        state.HistoricalForwardCurvesDataState.error = action.payload as string;
        state.HistoricalForwardCurvesDataState.data = [];
        state.HistoricalForwardCurvesDataState.title = '';
        state.HistoricalForwardCurvesDataState.description = '';
        state.HistoricalForwardCurvesDataState.meta = {
          currency_id: 0,
          security_id: 0,
          num_run_dates: 0,
        };
      })
      .addCase(getInterestRateAnalysisCurvesData.pending, (state) => {
        state.InterestRateAnalysisCurvesDataState.loading = true;
        state.InterestRateAnalysisCurvesDataState.error = null;
      })
      .addCase(
        getInterestRateAnalysisCurvesData.fulfilled,
        (state, action: PayloadAction<InterestRateAnalysisCurvesResponse>) => {
          state.InterestRateAnalysisCurvesDataState.data =
            action?.payload?.data.data;
          state.InterestRateAnalysisCurvesDataState.loading = false;
          state.InterestRateAnalysisCurvesDataState.error = null;
        },
      )
      .addCase(getInterestRateAnalysisCurvesData.rejected, (state, action) => {
        state.InterestRateAnalysisCurvesDataState.loading = false;
        state.InterestRateAnalysisCurvesDataState.error =
          action.payload as string;
        state.InterestRateAnalysisCurvesDataState.data = [];
      })
      .addCase(getInterestCostCalculationsData.pending, (state) => {
        state.InterestCostCalculationsDataState.loading = true;
        state.InterestCostCalculationsDataState.error = null;
      })
      .addCase(
        getInterestCostCalculationsData.fulfilled,
        (state, action: PayloadAction<InterestCostCalculationsResponse>) => {
          state.InterestCostCalculationsDataState.data =
            action.payload?.data?.daily_data;
          state.InterestCostCalculationsDataState.summary =
            action.payload.data.summary;
          state.InterestCostCalculationsDataState.loading = false;
          state.InterestCostCalculationsDataState.error = null;
        },
      )
      .addCase(getInterestCostCalculationsData.rejected, (state, action) => {
        state.InterestCostCalculationsDataState.loading = false;
        state.InterestCostCalculationsDataState.error =
          action.payload as string;
        state.InterestCostCalculationsDataState.data = [];
        state.InterestCostCalculationsDataState.summary = null;
      })
      .addCase(getForeignExchangeAnalysisData.pending, (state) => {
        state.ForeignExchangeAnalysisDataState.loading = true;
        state.ForeignExchangeAnalysisDataState.error = null;
      })
      .addCase(
        getForeignExchangeAnalysisData.fulfilled,
        (state, action: PayloadAction<ForeignExchangeAnalysisResponse>) => {
          const {
            summary,
            forward_rates,
            statistics,
            projections,
            chart_data,
            option_pricing,
            spot_rates,
          } = action.payload.data;

          state.ForeignExchangeAnalysisDataState.summary = summary;
          state.ForeignExchangeAnalysisDataState.forwardRatesData =
            forward_rates;
          state.ForeignExchangeAnalysisDataState.statisticsData = statistics;
          state.ForeignExchangeAnalysisDataState.projectionsData = projections;
          state.ForeignExchangeAnalysisDataState.chartData = chart_data;
          state.ForeignExchangeAnalysisDataState.optionPricingData =
            option_pricing;
          state.ForeignExchangeAnalysisDataState.spotRates = spot_rates;
          state.ForeignExchangeAnalysisDataState.loading = false;
          state.ForeignExchangeAnalysisDataState.error = null;
        },
      )
      .addCase(getForeignExchangeAnalysisData.rejected, (state, action) => {
        state.ForeignExchangeAnalysisDataState.loading = false;
        state.ForeignExchangeAnalysisDataState.error = action.payload as string;
        state.ForeignExchangeAnalysisDataState.summary = null;
        state.ForeignExchangeAnalysisDataState.statisticsData = null;
        state.ForeignExchangeAnalysisDataState.projectionsData = null;
        state.ForeignExchangeAnalysisDataState.chartData = null;
        state.ForeignExchangeAnalysisDataState.forwardRatesData = [];
        state.ForeignExchangeAnalysisDataState.optionPricingData = [];
        state.ForeignExchangeAnalysisDataState.spotRates = [];
      })
      .addCase(getCommoditiesForwardCurvesData.pending, (state) => {
        state.CommoditiesForwardCurvesDataState.loading = true;
        state.CommoditiesForwardCurvesDataState.error = null;
      })
      .addCase(
        getCommoditiesForwardCurvesData.fulfilled,
        (state, action: PayloadAction<CommoditiesForwardCurvesResponse>) => {
          state.CommoditiesForwardCurvesDataState.summary =
            action.payload.data.summary;
          state.CommoditiesForwardCurvesDataState.data =
            action.payload.data.curves;
          state.CommoditiesForwardCurvesDataState.loading = false;
          state.CommoditiesForwardCurvesDataState.error = null;
        },
      )
      .addCase(getCommoditiesForwardCurvesData.rejected, (state, action) => {
        state.CommoditiesForwardCurvesDataState.loading = false;
        state.CommoditiesForwardCurvesDataState.error =
          action.payload as string;
        state.CommoditiesForwardCurvesDataState.data = [];
        state.CommoditiesForwardCurvesDataState.summary = null;
      })
      .addCase(getCommoditiesVolatilityCurvesData.pending, (state) => {
        state.CommoditiesVolatilityCurvesDataState.loading = true;
        state.CommoditiesVolatilityCurvesDataState.error = null;
      })
      .addCase(
        getCommoditiesVolatilityCurvesData.fulfilled,
        (state, action: PayloadAction<CommoditiesVolatilityCurvesResponse>) => {
          state.CommoditiesVolatilityCurvesDataState.summary =
            action.payload.data.summary;
          state.CommoditiesVolatilityCurvesDataState.data =
            action.payload.data.curves;
          state.CommoditiesVolatilityCurvesDataState.loading = false;
          state.CommoditiesVolatilityCurvesDataState.error = null;
        },
      )
      .addCase(getCommoditiesVolatilityCurvesData.rejected, (state, action) => {
        state.CommoditiesVolatilityCurvesDataState.loading = false;
        state.CommoditiesVolatilityCurvesDataState.error =
          action.payload as string;
        state.CommoditiesVolatilityCurvesDataState.data = [];
        state.CommoditiesVolatilityCurvesDataState.summary = null;
      })
      .addCase(getMarketOverviewCurrencyRatesData.pending, (state) => {
        state.MarketOverviewCurrencyRateState.loading = true;
        state.MarketOverviewCurrencyRateState.error = null;
      })
      .addCase(
        getMarketOverviewCurrencyRatesData.fulfilled,
        (state, action: PayloadAction<MarketOverviewCurrencyRateResponse>) => {
          state.MarketOverviewCurrencyRateState.data = action.payload.data;
          state.MarketOverviewCurrencyRateState.loading = false;
          state.MarketOverviewCurrencyRateState.error = null;
        },
      )
      .addCase(getMarketOverviewCurrencyRatesData.rejected, (state, action) => {
        state.MarketOverviewCurrencyRateState.loading = false;
        state.MarketOverviewCurrencyRateState.error = action.payload as string;
      })
      .addCase(getMarketOverviewInterestRatesData.pending, (state) => {
        state.MarketOverviewInterestRateState.loading = true;
        state.MarketOverviewInterestRateState.error = null;
      })
      .addCase(
        getMarketOverviewInterestRatesData.fulfilled,
        (state, action: PayloadAction<MarketOverviewInterestRateResponse>) => {
          state.MarketOverviewInterestRateState.data = action.payload.data;
          state.MarketOverviewInterestRateState.loading = false;
          state.MarketOverviewInterestRateState.error = null;
        },
      )
      .addCase(getMarketOverviewInterestRatesData.rejected, (state, action) => {
        state.MarketOverviewInterestRateState.loading = false;
        state.MarketOverviewInterestRateState.error = action.payload as string;
      })
      .addCase(getFxForwardCurvesData.pending, (state) => {
        state.FxForwardCurvesDataState.loading = true;
        state.FxForwardCurvesDataState.error = null;
      })
      .addCase(
        getFxForwardCurvesData.fulfilled,
        (
          state,
          action: PayloadAction<{ run_date: string; pairs: FxForwardCurvePairData[] }>,
        ) => {
          state.FxForwardCurvesDataState.loading = false;
          state.FxForwardCurvesDataState.run_date = action.payload.run_date;
          state.FxForwardCurvesDataState.pairs = action.payload.pairs;
        },
      )
      .addCase(getFxForwardCurvesData.rejected, (state, action) => {
        state.FxForwardCurvesDataState.loading = false;
        state.FxForwardCurvesDataState.error = action.payload as string;
        state.FxForwardCurvesDataState.pairs = [];
        state.FxForwardCurvesDataState.run_date = null;
      });
  },
});

export const { clearFxCurrencyValidation, clearValidateIrIndexResult } =
  marketDataSlice.actions;
export default marketDataSlice.reducer;
